import api from './apiClient';
import logger from '../utils/logger';

const THUMBNAIL_WIDTH = 320;
const THUMBNAIL_HEIGHT = 180;
const THUMBNAIL_QUALITY = 0.85;

function bakeVideoFrames(container: HTMLElement): Array<{ video: HTMLVideoElement; originalBg: string; originalBgSize: string }> {
  const videos = container.querySelectorAll('video');
  const baked: Array<{ video: HTMLVideoElement; originalBg: string; originalBgSize: string }> = [];
  const tempCanvas = document.createElement('canvas');
  const tempCtx = tempCanvas.getContext('2d');

  if (!tempCtx) return baked;

  videos.forEach(video => {
    if (!video.videoWidth || !video.videoHeight) return;

    tempCanvas.width = video.videoWidth;
    tempCanvas.height = video.videoHeight;

    try {
      tempCtx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
      const dataUrl = tempCanvas.toDataURL('image/png');

      baked.push({
        video,
        originalBg: video.style.backgroundImage,
        originalBgSize: video.style.backgroundSize,
      });

      video.style.backgroundImage = `url(${dataUrl})`;
      video.style.backgroundSize = 'cover';
    } catch (e) {
      logger.warn('[CompositorThumbnail] Could not bake video frame (CORS?):', e);
    }

    tempCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
  });

  return baked;
}

function restoreVideoFrames(baked: Array<{ video: HTMLVideoElement; originalBg: string; originalBgSize: string }>) {
  baked.forEach(({ video, originalBg, originalBgSize }) => {
    video.style.backgroundImage = originalBg;
    video.style.backgroundSize = originalBgSize;
  });
}

export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export async function captureCompositorThumbnail(
  containerNode: HTMLDivElement
): Promise<Blob | null> {
  try {
    const captureTarget = containerNode.querySelector('[data-remotion-canvas]') as HTMLElement
      || containerNode.firstElementChild as HTMLElement;

    if (!captureTarget) {
      logger.warn('[CompositorThumbnail] No renderable element found in compositor container');
      return null;
    }

    const videoCount = containerNode.querySelectorAll('video').length;
    logger.info('[CompositorThumbnail] Capturing composited frame via html2canvas', {
      targetTag: captureTarget.tagName,
      targetWidth: captureTarget.clientWidth,
      targetHeight: captureTarget.clientHeight,
      videoCount,
    });

    const bakedVideos = bakeVideoFrames(captureTarget);
    logger.info('[CompositorThumbnail] Baked video frames as background images', { count: bakedVideos.length });

    let capturedCanvas: HTMLCanvasElement;
    try {
      const { default: html2canvas } = await import('html2canvas');
      capturedCanvas = await html2canvas(captureTarget, {
        width: captureTarget.clientWidth,
        height: captureTarget.clientHeight,
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: '#000000',
        scale: Math.max(1, THUMBNAIL_WIDTH / captureTarget.clientWidth),
      });
    } finally {
      restoreVideoFrames(bakedVideos);
    }

    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = THUMBNAIL_WIDTH;
    outputCanvas.height = THUMBNAIL_HEIGHT;
    const ctx = outputCanvas.getContext('2d');
    if (!ctx) {
      logger.error('[CompositorThumbnail] Failed to get output canvas context');
      return null;
    }
    ctx.drawImage(capturedCanvas, 0, 0, THUMBNAIL_WIDTH, THUMBNAIL_HEIGHT);

    return new Promise<Blob | null>((resolve) => {
      outputCanvas.toBlob(
        (blob) => {
          if (blob) {
            logger.info('[CompositorThumbnail] Thumbnail captured', {
              size: blob.size,
              dimensions: `${THUMBNAIL_WIDTH}x${THUMBNAIL_HEIGHT}`,
            });
          }
          resolve(blob);
        },
        'image/jpeg',
        THUMBNAIL_QUALITY
      );
    });
  } catch (error) {
    logger.error('[CompositorThumbnail] Capture failed:', error);
    return null;
  }
}

export async function uploadCompositorThumbnail(
  blob: Blob,
  projectId: string | number,
  sceneId: string | number
): Promise<string | null> {
  try {
    const { data: urlData } = await api.post('/api/objects/upload-url', {
      fileName: `compositor-thumbnail-${sceneId}.jpg`,
      contentType: 'image/jpeg',
      projectId: Number(projectId),
      sceneId: Number(sceneId),
    });

    const { uploadUrl, fileUrl, key } = urlData;

    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'image/jpeg',
      },
      body: blob,
    });

    if (!uploadResponse.ok) {
      logger.error('[CompositorThumbnail] S3 upload failed:', uploadResponse.status);
      return null;
    }

    logger.info('[CompositorThumbnail] Thumbnail uploaded to S3', { key, fileUrl });

    try {
      await api.put(`/api/scenes/${sceneId}`, {
        thumbnailUrl: fileUrl,
      });
      logger.info('[CompositorThumbnail] Scene thumbnailUrl updated in database', { sceneId, thumbnailUrl: fileUrl });
    } catch (dbError) {
      logger.error('[CompositorThumbnail] Failed to update scene thumbnailUrl:', dbError);
    }

    return fileUrl;
  } catch (error) {
    logger.error('[CompositorThumbnail] Upload failed:', error);
    return null;
  }
}

export async function captureAndUploadCompositorThumbnail(
  containerNode: HTMLDivElement,
  projectId: string | number,
  sceneId: string | number
): Promise<string | null> {
  const blob = await captureCompositorThumbnail(containerNode);
  if (!blob) {
    logger.warn('[CompositorThumbnail] No thumbnail captured, skipping upload');
    return null;
  }

  return uploadCompositorThumbnail(blob, projectId, sceneId);
}
